import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineBell,
  HiOutlineWallet,
  HiOutlineArrowRightOnRectangle,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { TbMenu } from "react-icons/tb";
import ChevronDownSmall from "../../design-system/icons/ChevronDownSmall.jsx";
import ChevronTopSmall from "../../design-system/icons/ChevronTopSmall.jsx";
import ChevronRightSmall from "../../design-system/icons/ChevronRightSmall.jsx";
import SearchInput from "../../design-system/input-text/SearchInput.jsx";
import { baseAxios } from "../../apis";

const SPOT_URL = process.env.REACT_APP_SPOT_URL || "https://stg2.xenorize.com/dashboard";
const SPOT_ROOT = SPOT_URL.replace(/\/dashboard$/, "");

const MARKETS = [
  {
    key: "spot",
    label: "Spot",
    description: "For Newbie and Moderate",
  },
  {
    key: "leverage",
    label: "Margin and Leverage",
    badge: "BETA",
    description: "Higher risk — requires acknowledgement",
  },
];

function MarketIcon() {
  return (
    <span className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 inline-block shrink-0" />
  );
}

function MarketSwitcher() {
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < 760;
    if (isMobile) {
      setPanelStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: 12,
        right: 12,
        zIndex: 10000,
      });
    } else {
      const panelWidth = 288;
      setPanelStyle({
        position: "fixed",
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - panelWidth),
        width: panelWidth,
        zIndex: 10000,
      });
    }
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => updatePosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        triggerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div ref={triggerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 desktop:gap-2 rounded-xl border border-gray-200 px-1.5 py-1 desktop:px-3 desktop:py-1.5 text-sm font-medium text-[#21272A] bg-white shrink-0"
      >
        <MarketIcon />
        <span className="hidden desktop:inline">Leverage</span>
        <span className="hidden desktop:inline text-[10px] font-semibold text-[#5B3DF6] bg-[#EFEAFE] rounded-full px-1.5 py-0.5">BETA</span>
        {open ? (
          <ChevronTopSmall className="w-4 h-4 text-[#697077]" />
        ) : (
          <ChevronDownSmall className="w-4 h-4 text-[#697077]" />
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="bg-white rounded-xl border border-gray-200 shadow-lg p-2"
          style={panelStyle || { position: "fixed", top: 64, right: 24, width: 288, zIndex: 10000 }}
        >
          <div className="text-[10px] font-semibold text-[#A4A9AC] uppercase tracking-wide px-2 py-1">
            Choose Market
          </div>

          {/* Spot — use <a> so the browser handles navigation directly */}
          <a
            href={SPOT_URL}
            onClick={() => setOpen(false)}
            className="w-full flex items-start gap-3 rounded-lg px-3 py-2 text-left border border-transparent hover:bg-gray-50 no-underline"
          >
            <span className="mt-0.5"><MarketIcon /></span>
            <span className="flex-1">
              <span className="block text-sm font-medium text-[#21272A]">Spot</span>
              <span className="block text-xs text-[#697077] mt-0.5">For Newbie and Moderate</span>
            </span>
            <span className="w-4 h-4 rounded-full border-2 mt-1 shrink-0 border-gray-300" />
          </a>

          {/* Leverage — current market, no navigation */}
          <div className="w-full flex items-start gap-3 rounded-lg px-3 py-2 text-left border border-[#5B3DF6] bg-[#F5F3FF]">
            <span className="mt-0.5"><MarketIcon /></span>
            <span className="flex-1">
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-medium text-[#21272A]">Margin and Leverage</span>
                <span className="text-[10px] font-semibold text-[#5B3DF6] bg-[#EFEAFE] rounded-full px-1.5 py-0.5">BETA</span>
              </span>
              <span className="block text-xs text-[#697077] mt-0.5">Higher risk — requires acknowledgement</span>
            </span>
            <span className="w-4 h-4 rounded-full border-2 mt-1 shrink-0 flex items-center justify-center border-[#5B3DF6]">
              <span className="w-2 h-2 rounded-full bg-[#5B3DF6]" />
            </span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function Topbar({ user }) {
  const firstName = user?.first_name || user?.email?.split("@")[0] || "";

  const [avatarOpen, setAvatarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await baseAxios.get("/api/csrf-cookie/");
      const csrfToken =
        (document.cookie.match(/csrftoken=([^;]+)/) || [])[1] || "";
      await baseAxios.post(
        "/api/auth/logout/",
        {},
        { headers: { "X-CSRFToken": csrfToken } }
      );
    } catch (_) {}
    window.location.href = `${SPOT_ROOT}/api/sso-logout/`;
  };

  return (
    <header className="flex items-center gap-2 px-4 py-3 desktop:gap-4 desktop:px-6 desktop:py-4 bg-white border-b border-gray-200">
      <h1 className="text-sm desktop:text-base font-semibold text-[#21272A] shrink-0 truncate">
        Welcome Back, {firstName}!
      </h1>

      {/* search bar — hidden for now */}

      <div className="flex-1" />

      <MarketSwitcher />

      {/* Wallet — desktop only */}
      <button className="hidden desktop:flex w-9 h-9 rounded-full border border-gray-200 items-center justify-center text-[#697077]">
        <HiOutlineWallet className="w-4 h-4" />
      </button>

      {/* bell sits bare on mobile/tablet, ringed on desktop */}
      <button className="relative w-8 h-8 desktop:w-9 desktop:h-9 rounded-full desktop:border desktop:border-gray-200 flex items-center justify-center text-[#697077] shrink-0">
        <HiOutlineBell className="w-[18px] h-[18px] desktop:w-4 desktop:h-4" />
        <span className="absolute top-1 right-1 desktop:top-1.5 desktop:right-1.5 w-1.5 h-1.5 rounded-full bg-red-500" />
      </button>

      <div className="relative shrink-0">
        <div
          onClick={() => setAvatarOpen(o => !o)}
          className="w-8 h-8 desktop:w-9 desktop:h-9 rounded-full bg-[#5B3DF6] text-white flex items-center justify-center text-sm font-medium cursor-pointer select-none"
        >
          {firstName.charAt(0).toUpperCase() || "?"}
        </div>
        {avatarOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setAvatarOpen(false)} />
            <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-xl border border-gray-200 shadow-lg py-1 z-50">
              <div className="px-3 py-1.5 text-xs text-[#697077] border-b border-gray-100 truncate">
                {user?.email}
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
              >
                <HiOutlineArrowRightOnRectangle className="w-4 h-4" />
                Logout
              </button>
            </div>
          </>
        )}
      </div>

      {/* Product Tour — hidden for now */}

      {/* Hamburger — mobile/tablet only */}
      <button aria-label="Menu" className="desktop:hidden w-8 h-8 flex items-center justify-center text-[#697077] shrink-0">
        <TbMenu className="w-5 h-5" />
      </button>
    </header>
  );
}
